import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@shared/contexts/ThemeContext';
import PgkbLogo from '@shared/components/PgkbLogo';
import LanguageSwitcher from '@shared/components/LanguageSwitcher';
import { Shield, Zap, RotateCcw, ArrowRight, Moon, Sun } from 'lucide-react';

const LandingPage: React.FC = () => {
  const { t } = useTranslation('landing');
  const { theme, toggleTheme } = useTheme();
  const [isVisible, setIsVisible] = useState({ features: false, cta: false });
  const featuresRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) observer.observe(featuresRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-surface-light font-sans text-slate-900 selection:bg-accent-soft-light selection:text-brand-900 scroll-smooth dark:bg-surface-dark dark:text-white dark:selection:bg-accent-soft-dark">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-surface-light/90 dark:bg-surface-dark/90 backdrop-blur-lg border-b border-surface-border-light/50 dark:border-surface-border-dark/50">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex flex-wrap justify-between min-h-20 py-3 gap-2 sm:h-20 sm:py-0 items-center">
            <div className="flex shrink-0 items-center gap-3">
              <PgkbLogo size="md" />
            </div>

            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
              <a
                href="#features"
                className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                {t('nav.features')}
              </a>
              <Link
                to="/pricing"
                className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              >
                {t('nav.pricing')}
              </Link>
            </div>

            <div className="flex items-center gap-1 sm:gap-4">
              <LanguageSwitcher variant="minimal" align="start" />
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-all"
                aria-label={t('common:labels.toggleTheme', 'Toggle theme')}
              >
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <Link
                to="/login"
                className="shrink-0 whitespace-nowrap px-2 py-2 text-xs font-semibold text-slate-600 transition-colors hover:text-brand-600 dark:text-slate-300 dark:hover:text-brand-400 sm:px-4 sm:text-sm"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="shrink-0 whitespace-nowrap rounded-xl bg-brand-600 px-2 py-2.5 text-xs font-semibold text-white shadow-lg shadow-brand-600/25 transition-all duration-150 hover:scale-[1.02] hover:bg-brand-700 hover:shadow-brand-600/30 active:scale-[0.98] sm:px-5 sm:text-sm"
              >
                {t('nav.getStarted')}
              </Link>
            </div>

            <div className="flex w-full items-center justify-center gap-2 border-t border-surface-border-light/50 pt-1 text-sm font-medium text-text-muted-light dark:border-surface-border-dark/50 dark:text-text-muted-dark md:hidden">
              <a
                href="#features"
                className="inline-flex min-h-11 items-center rounded-lg px-4 transition-colors hover:bg-surface-2-light hover:text-brand-600 dark:hover:bg-surface-2-dark dark:hover:text-brand-400"
              >
                {t('nav.features')}
              </a>
              <Link
                to="/pricing"
                className="inline-flex min-h-11 items-center rounded-lg px-4 transition-colors hover:bg-surface-2-light hover:text-brand-600 dark:hover:bg-surface-2-dark dark:hover:text-brand-400"
              >
                {t('nav.pricing')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 lg:pt-52 lg:pb-44">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-brand-500/[0.03] dark:bg-brand-500/[0.05] rounded-full blur-[120px] pointer-events-none" />

        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 dark:text-white tracking-tight leading-[1.1] mb-8">
              {t('hero.title')}{' '}
              <span className="text-brand-600 dark:text-brand-400">{t('hero.titleHighlight')}</span>
            </h1>

            <p className="text-xl text-text-muted-light dark:text-text-muted-dark leading-relaxed mb-14 max-w-xl mx-auto">
              {t('hero.subtitle')}
            </p>

            <Link
              to="/register"
              className="group inline-flex items-center gap-3 bg-brand-600 text-white px-8 py-4 rounded-2xl text-base font-semibold shadow-xl shadow-brand-600/25 hover:bg-brand-700 hover:shadow-brand-600/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-150"
            >
              {t('hero.cta')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16 text-text-subtle-light dark:text-text-subtle-dark">
            <TrustItem icon={<Shield className="w-5 h-5" />} text={t('trust.encryption')} />
            <TrustItem icon={<Zap className="w-5 h-5" />} text={t('trust.recovery')} />
            <TrustItem icon={<RotateCcw className="w-5 h-5" />} text={t('trust.backups')} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        ref={featuresRef as React.RefObject<HTMLDivElement>}
        className={`py-28 lg:py-36 bg-surface-2-light dark:bg-surface-2-dark border-y border-surface-border-light/50 dark:border-surface-border-dark/50 transition-all duration-1000 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-sm font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider mb-4">
              {t('featuresSection.subtitle')}
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('featuresSection.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <FeatureCard
              icon={<Shield className="w-7 h-7" />}
              title={t('features.secure.title')}
              description={t('features.secure.description')}
            />
            <FeatureCard
              icon={<RotateCcw className="w-7 h-7" />}
              title={t('features.automated.title')}
              description={t('features.automated.description')}
            />
            <FeatureCard
              icon={<Zap className="w-7 h-7" />}
              title={t('features.recovered.title')}
              description={t('features.recovered.description')}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        id="cta"
        ref={ctaRef as React.RefObject<HTMLDivElement>}
        className={`py-28 lg:py-36 transition-all duration-1000 ${isVisible.cta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="relative bg-brand-600 dark:bg-brand-700 rounded-[2.5rem] px-8 py-20 lg:py-28 text-center text-white overflow-hidden shadow-2xl shadow-brand-600/30">
            <div className="absolute top-0 right-0 -translate-y-1/3 translate-x-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-96 h-96 bg-brand-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-xl mx-auto">
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                {t('ctaSection.title')}
              </h2>
              <p className="text-brand-100 text-lg mb-10 opacity-90">{t('ctaSection.subtitle')}</p>
              <Link
                to="/register"
                className="group inline-flex items-center gap-3 bg-white text-brand-600 px-8 py-4 rounded-2xl text-base font-semibold shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-150"
              >
                {t('ctaSection.button')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-card-light dark:bg-surface-card-dark border-t border-surface-border-light dark:border-surface-border-dark py-12">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <PgkbLogo size="sm" />
            </div>

            <div className="flex items-center gap-8 text-sm font-medium text-text-subtle-light dark:text-text-subtle-dark">
              <Link
                to="/privacy"
                className="hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                {t('footer.privacy')}
              </Link>
              <Link
                to="/terms"
                className="hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                {t('footer.terms')}
              </Link>
              <Link
                to="/cookies"
                className="hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                {t('footer.cookies')}
              </Link>
            </div>

            <p className="text-sm text-text-subtle-light dark:text-text-subtle-dark">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function TrustItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-brand-600 dark:text-brand-400">{icon}</span>
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="group bg-surface-card-light dark:bg-surface-card-dark p-10 lg:p-12 rounded-[2rem] border border-surface-border-light dark:border-surface-border-dark shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
      <div className="w-14 h-14 bg-accent-soft-light dark:bg-accent-soft-dark rounded-2xl flex items-center justify-center text-brand-600 dark:text-brand-400 mb-6 group-hover:bg-brand-600 group-hover:text-white group-hover:scale-110 transition-all duration-300">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{title}</h3>
      <p className="text-text-muted-light dark:text-text-muted-dark leading-relaxed">
        {description}
      </p>
    </div>
  );
}

export default LandingPage;
